/**
 * TenantPrivilegeDrawer — Side panel for creating/editing/viewing tenant privileges.
 */
import { useState, useEffect, useMemo, type JSX } from 'react'
import { Checkbox } from 'primereact/checkbox'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Steps } from 'primereact/steps'

import type { TenantPrivilege } from '../../hooks/useTenantPrivileges.js'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TenantPrivilegeDrawerProps {
  open: boolean
  onClose: () => void
  mode?: 'create' | 'view' | 'edit'
  privilege?: TenantPrivilege | undefined
  onSave: (data: {
    status: boolean
    name: string
    description: string
    privilegesDetail: Record<string, unknown>
  }) => Promise<void>
  loading?: boolean
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PERMISSION_CATEGORIES = [
  {
    id: 'domain_management',
    name: 'Domain Management',
    icon: 'pi pi-cog',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: [
      'Organization Details', 'Branch', 'Location', 'Department',
      'User Privileges', 'User Portal - IP Whitelist', 'Break Codes', 'Password Policy',
      'API Information', 'Voice Gateway', 'Voice Gateway Whitelist', 'Email Gateways',
      'SMS Gateways', 'Time Conditions', 'Extension', 'Scripts Scheduler'
    ]
  },
  {
    id: 'user_management',
    name: 'User Management',
    icon: 'pi pi-users',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Users', 'Teams', 'Skills', 'Approvers']
  },
  {
    id: 'telephony_management',
    name: 'Telephony Management',
    icon: 'pi pi-phone',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: [
      'Inbound Numbers', 'Outbound Call Routing', 'Advance Call Flow', 'FIFO Queues',
      'Ring Groups', 'IVR', 'External Call Forward', 'MOH Groups', 'Voice Files',
      'Play n Go', 'Blacklist Caller', 'Transfers', 'Speed Dial', 'VIP Callers',
      'Special Inbound Routing', 'Conference'
    ]
  },
  {
    id: 'voice_contact_management',
    name: 'Voice Contact Management',
    icon: 'pi pi-microphone',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: [
      'Inbound Campaign', 'Outbound Campaign', 'Outbound IVR', 'Fax Campaign',
      'Contact Center Queue', 'Dispositions', 'Dispositions Group'
    ]
  },
  {
    id: 'web_contact_management',
    name: 'Campaign Management',
    icon: 'pi pi-globe',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: [
      'Campaign', 'Client Access Level', 'Conversation Reopen rules',
      'Web Signup/Login', 'Permissions', 'Conversation Dynamic Fields', 'Content Management'
    ]
  },
  {
    id: 'email_contact_management',
    name: 'Email Contact Management',
    icon: 'pi pi-envelope',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Campaign', 'Email IDs']
  },
  {
    id: 'twitter_contact_management',
    name: 'Twitter Contact Management',
    icon: 'pi pi-twitter',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Campaign', 'Twitter IDs']
  },
  {
    id: 'facebook_contact_management',
    name: 'Facebook Contact Management',
    icon: 'pi pi-facebook',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Campaign', 'Facebook IDs']
  },
  {
    id: 'sms_contact_management',
    name: 'SMS Contact Management',
    icon: 'pi pi-comment',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Campaign', 'SMS Accounts']
  },
  {
    id: 'whatsapp_contact_management',
    name: 'WhatsApp Contact Management',
    icon: 'pi pi-whatsapp',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Campaign', 'WhatsApp Accounts']
  },
  {
    id: 'youtube_contact_management',
    name: 'YouTube Contact Management',
    icon: 'pi pi-youtube',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Campaign', 'Youtube IDs']
  },
  {
    id: 'livechat_contact_management',
    name: 'LiveChat Contact Management',
    icon: 'pi pi-comments',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Campaign', 'Chat Channel']
  },
  {
    id: 'interaction_management',
    name: 'Interaction Management',
    icon: 'pi pi-sitemap',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: [
      'Type', 'Category', 'Sub Category', 'Status', 'State', 'Impact',
      'Urgency', 'Priority', 'Complexity', 'Qk Replies', 'Qk Scripts',
      'Qk Replies Category', 'Qk Replies Sub-Category', 'Approval States',
      'Approval Level', 'Tags', 'Additional Properties', 'Root Cause Analysis'
    ]
  },
  {
    id: 'task_management',
    name: 'Task Management',
    icon: 'pi pi-check-square',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Type', 'Status', 'Priority', 'Tasks', 'Task Group', 'Check List']
  },
  {
    id: 'change_management',
    name: 'Change Management',
    icon: 'pi pi-sync',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: [
      'Type', 'Category', 'Impact', 'Urgency', 'Priority', 'Status',
      'Approval States', 'Approval Level', 'Form'
    ]
  },
  {
    id: 'problem_management',
    name: 'Problem Management',
    icon: 'pi pi-exclamation-circle',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Status', 'State', 'Impact', 'Priority', 'Complexity']
  },
  {
    id: 'productivity_management',
    name: 'Productivity Management',
    icon: 'pi pi-chart-line',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: [
      'Omnichannel Queue', 'Routing Rules', 'AI Routing Rules', 'AI Agent Teams',
      'SLA Policy', 'SLA Config', 'Automations', 'Workflow Rules', 'Scripts',
      'Change Workflow Rules', 'Change Routing Rules'
    ]
  },
  {
    id: 'template_management',
    name: 'Template Management',
    icon: 'pi pi-copy',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: [
      'Interaction Template', 'Interaction Print Template', 'Change Template',
      'Problem Template', 'Email Templates', 'SMS Templates', 'WhatsApp Templates', 'Web Templates'
    ]
  },
  {
    id: 'client_management',
    name: 'Client Management',
    icon: 'pi pi-users',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Clients', 'Client Fields', 'Client Groups', 'Upload Clients']
  },
  {
    id: 'faq_management',
    name: 'FAQ Management',
    icon: 'pi pi-question-circle',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['FAQs', 'Category']
  },
  {
    id: 'knowledgebase_management',
    name: 'Knowledgebase Management',
    icon: 'pi pi-book',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['KBs', 'Category', 'Kb_Reorder']
  },
  {
    id: 'announcement_management',
    name: 'Announcement Management',
    icon: 'pi pi-megaphone',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Announcements', 'Category']
  },
  {
    id: 'news_management',
    name: 'News Management',
    icon: 'pi pi-file',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['News', 'Category']
  },
  {
    id: 'quality_management',
    name: 'Quality Management',
    icon: 'pi pi-star',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Rules', 'QMForm']
  },
  {
    id: 'customer_satisfaction_survey',
    name: 'Customer Satisfaction Survey',
    icon: 'pi pi-thumbs-up',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Type']
  },
  {
    id: 'lead_management',
    name: 'Lead Management',
    icon: 'pi pi-briefcase',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Leads', 'Fields', 'Groups']
  },
  {
    id: 'dnc_management',
    name: 'DNC Management',
    icon: 'pi pi-ban',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Client', 'DNC List']
  },
  {
    id: 'asset_management',
    name: 'Asset Management',
    icon: 'pi pi-box',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Type', 'Brand', 'Status', 'Owner', 'Vendor', 'Floor', 'Asset']
  },
  {
    id: 'report_management',
    name: 'Report Management',
    icon: 'pi pi-chart-pie',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-500',
    permissions: ['Report Management']
  }
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WIZARD_STEPS = [
  { label: 'Basic Info' },
  { label: 'Permissions' }
]

export default function TenantPrivilegeDrawer({
  open,
  onClose,
  mode = 'create',
  privilege,
  onSave,
  loading = false,
}: TenantPrivilegeDrawerProps): JSX.Element {
  const [activeStep, setActiveStep] = useState(0)

  const [status, setStatus] = useState(true)
  const [criticalAccess, setCriticalAccess] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])

  const totalPermissions = useMemo(() => {
    return PERMISSION_CATEGORIES.reduce((acc, cat) => acc + cat.permissions.length, 0)
  }, [])

  useEffect(() => {
    if (open) {
      if ((mode === 'edit' || mode === 'view') && privilege) {
        setStatus(privilege.status)
        setName(privilege.name)
        setDescription(privilege.description ?? '')
        
        const isCritical = Boolean(privilege.privilegesDetail?.criticalAccess)
        setCriticalAccess(isCritical)
        
        const savedPerms = privilege.privilegesDetail 
          ? Object.keys(privilege.privilegesDetail).filter(k => privilege.privilegesDetail![k] === true && k !== 'criticalAccess') 
          : []
        setSelectedPermissions(savedPerms)
        setActiveStep(0)
      } else {
        setStatus(true)
        setCriticalAccess(false)
        setName('')
        setDescription('')
        setSelectedPermissions([])
        setActiveStep(0)
      }
    }
  }, [open, mode, privilege])

  const handleSave = async (): Promise<void> => {
    const privilegesDetail: Record<string, unknown> = {
      criticalAccess
    }
    selectedPermissions.forEach(perm => {
      privilegesDetail[perm] = true
    })

    await onSave({
      status,
      name,
      description,
      privilegesDetail,
    })
  }

  const togglePermission = (perm: string) => {
    if (mode === 'view') return
    setSelectedPermissions(prev => 
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
    )
  }

  const selectCategory = (categoryId: string) => {
    if (mode === 'view') return
    const cat = PERMISSION_CATEGORIES.find(c => c.id === categoryId)
    if (!cat) return
    const toAdd = cat.permissions.filter(p => !selectedPermissions.includes(p))
    if (toAdd.length) {
      setSelectedPermissions(prev => [...prev, ...toAdd])
    }
  }

  const clearCategory = (categoryId: string) => {
    if (mode === 'view') return
    const cat = PERMISSION_CATEGORIES.find(c => c.id === categoryId)
    if (!cat) return
    setSelectedPermissions(prev => prev.filter(p => !cat.permissions.includes(p)))
  }

  const selectAllPermissions = () => {
    if (mode === 'view') return
    const all = PERMISSION_CATEGORIES.flatMap(c => c.permissions)
    setSelectedPermissions(all)
  }

  const clearAllPermissions = () => {
    if (mode === 'view') return
    setSelectedPermissions([])
  }

  const isViewMode = mode === 'view'
  const isEditMode = mode === 'edit'

  if (!open) return <></>

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[9998] bg-black/40" onClick={onClose} />

      {/* Drawer Panel */}
      <aside className="fixed right-0 top-0 z-[9999] flex h-[100vh] w-full max-w-4xl flex-col bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#3454cf] flex items-center justify-center">
              <i className="pi pi-shield text-white text-sm" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">
                {isEditMode ? 'Edit Privilege' : mode === 'create' ? 'New Privilege' : 'View Privilege'}
              </h2>
              <p className="text-xs text-gray-400">Step {activeStep + 1} of {WIZARD_STEPS.length}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all"
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="border-b border-gray-100 px-6 py-3 shrink-0 bg-white">
          <Steps
            model={WIZARD_STEPS}
            activeIndex={activeStep}
            onSelect={(e) => setActiveStep(e.index)}
            readOnly={false}
            pt={{
              root: { className: 'w-full' },
              menu: { className: 'flex flex-row gap-2' },
              item: { className: 'flex-1' },
              label: { className: 'text-sm font-medium text-gray-400' },
              active: { className: 'text-[#3454cf] font-semibold' }
            } as any}
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {activeStep === 0 ? (
            <div className="space-y-5">
              {/* Privilege Identity */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-50">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Privilege Identity</h3>
                </div>
                <div className="p-5 space-y-5">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-semibold text-gray-700">Privilege Name <span className="text-red-500">*</span></label>
                    <InputText
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter privilege name"
                      disabled={isViewMode}
                      pt={{
                        root: { className: 'w-full !rounded-xl !border !border-gray-200 !bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:!border-[#f6931c] focus:!bg-white focus:!ring-2 focus:!ring-[#f6931c]/10' }
                      } as any}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-semibold text-gray-700">Description <span className="text-red-500">*</span></label>
                    <InputTextarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      placeholder="Describe what this privilege allows users to do"
                      disabled={isViewMode}
                      pt={{
                        root: { className: 'w-full !rounded-xl !border !border-gray-200 !bg-gray-50/50 px-4 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none transition-all focus:!border-[#f6931c] focus:!bg-white focus:!ring-2 focus:!ring-[#f6931c]/10' }
                      } as any}
                    />
                  </div>
                </div>
              </div>

              {/* Configuration */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-50">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Configuration</h3>
                </div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className={`flex items-center justify-between p-4 rounded-sm border transition-all ${status ? 'bg-emerald-50/30 border-emerald-100' : 'bg-gray-50/50 border-gray-100'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${status ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                        <i className={status ? 'pi pi-check text-sm' : 'pi pi-times text-sm'} />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-800 text-sm">{status ? 'Active Status' : 'Inactive Status'}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{status ? 'Privilege is active' : 'Privilege is inactive'}</div>
                      </div>
                    </div>
                    <Checkbox inputId="status-cb" checked={status} onChange={(e) => !isViewMode && setStatus(!!e.checked)} />
                  </div>
                  <div className={`flex items-center justify-between p-4 rounded-sm border transition-all ${criticalAccess ? 'bg-amber-50/30 border-amber-100' : 'bg-gray-50/50 border-gray-100'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${criticalAccess ? 'bg-amber-100 text-amber-600' : 'bg-gray-100 text-gray-400'}`}>
                        <i className="pi pi-exclamation-triangle text-sm" />
                      </div>
                      <div>
                        <div className="font-semibold text-gray-800 text-sm">Critical Gateway</div>
                        <div className="text-xs text-gray-400 mt-0.5">{criticalAccess ? 'Enabled' : 'Disabled'}</div>
                      </div>
                    </div>
                    <Checkbox inputId="critical-cb" checked={criticalAccess} onChange={(e) => !isViewMode && setCriticalAccess(!!e.checked)} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Summary bar */}
              <div className="flex items-center justify-between rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Domain Permissions</h3>
                  <p className="text-sm text-gray-400">Select specific permissions for this privilege</p>
                </div>
                <div className="flex items-center rounded-full bg-[#3454cf]/5 px-4 py-1.5 text-sm font-bold text-[#3454cf]">
                  <i className="pi pi-circle-fill mr-2 text-[8px]" />
                  {selectedPermissions.length} / {totalPermissions} selected
                </div>
              </div>

              {/* Quick actions */}
              <div className="flex items-center justify-between rounded-xl border border-gray-100 bg-white p-4">
                <span className="text-sm font-medium text-gray-500 ml-2">Quick actions for all permissions</span>
                <div className="flex gap-2">
                  <button
                    onClick={selectAllPermissions}
                    disabled={isViewMode}
                    className="inline-flex items-center gap-1.5 bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 disabled:text-gray-400 text-white text-xs font-semibold px-3 py-2 rounded-sm transition-all"
                  >
                    <i className="pi pi-check text-xs" />
                    Select All
                  </button>
                  <button
                    onClick={clearAllPermissions}
                    disabled={isViewMode}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50"
                  >
                    <i className="pi pi-times text-xs" />
                    Clear All
                  </button>
                </div>
              </div>

              {/* Permission Categories */}
              <div className="space-y-5">
                {PERMISSION_CATEGORIES.map((category) => {
                  const isAllSelected = category.permissions.length > 0 && category.permissions.every(p => selectedPermissions.includes(p))
                  return (
                    <div key={category.id} className="rounded-xl border border-gray-100 bg-white overflow-hidden">
                      <div className="flex items-center justify-between border-b border-gray-50 px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#3454cf]/5">
                            <i className={`${category.icon} text-[#3454cf] text-sm`} />
                          </div>
                          <div>
                            <h4 className="text-sm font-bold text-gray-900">{category.name}</h4>
                            <p className="text-xs text-gray-400">{category.permissions.length} permissions</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => selectCategory(category.id)}
                            disabled={isViewMode}
                            className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-sm transition-all ${isAllSelected ? 'bg-[#3454cf]/5 text-[#3454cf] border border-[#3454cf]/20' : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
                          >
                            <i className="pi pi-check text-[10px]" />
                            All
                          </button>
                          <button
                            onClick={() => clearCategory(category.id)}
                            disabled={isViewMode}
                            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-sm border border-gray-200 text-gray-500 hover:bg-gray-50 transition-all"
                          >
                            <i className="pi pi-times text-[10px]" />
                            Clear
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 bg-gray-50/30">
                        {category.permissions.map((perm) => {
                          const checked = selectedPermissions.includes(perm)
                          return (
                            <div
                              key={perm}
                              className={`flex items-center gap-3 rounded-sm border p-3 transition-all cursor-pointer ${
                                checked ? 'border-[#3454cf]/30 bg-white shadow-sm' : 'border-gray-100 bg-white hover:border-[#3454cf]/20'
                              }`}
                              onClick={() => togglePermission(perm)}
                            >
                              <Checkbox checked={checked} readOnly className="pointer-events-none" />
                              <span className="text-xs font-medium text-gray-700 cursor-pointer select-none">{perm}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4 bg-white">
          <div>
            {activeStep > 0 && (
              <button
                onClick={() => setActiveStep(activeStep - 1)}
                disabled={loading}
                className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
              >
                <i className="pi pi-arrow-left text-xs" />
                Back
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={loading}
              className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-sm border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            {activeStep === 0 ? (
              <button
                onClick={() => setActiveStep(1)}
                disabled={!name.trim()}
                className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-sm hover:shadow-md"
              >
                Next
                <i className="pi pi-arrow-right text-xs" />
              </button>
            ) : (
              !isViewMode && (
                <button
                  onClick={handleSave}
                  disabled={!name.trim() || loading}
                  className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-sm hover:shadow-md"
                >
                  {loading ? <i className="pi pi-spin pi-spinner text-xs" /> : <i className="pi pi-check text-xs" />}
                  {isEditMode ? 'Update Privilege' : 'Create Privilege'}
                </button>
              )
            )}
          </div>
        </div>
      </aside>
    </>
  )
}
